import React, { useState, useEffect, useCallback, useRef } from "react";
import { invoicingAPI } from "../../utils/invoicingAPI";
import { LoadingSpinner } from "../../components/Loaders";
import { EmptyState, ErrorState } from "../../components/EmptyStates";
import Pagination from "../../components/Pagination";
import {
  FiSearch,
  FiX,
  FiShield,
  FiRefreshCw,
  FiFilter,
  FiClock,
  FiUser,
} from "react-icons/fi";
import { useTranslation } from "react-i18next";
import "./AuditLogs.css";

const ACTION_COLORS = {
  INVOICE_CREATED: { bg: "rgba(16, 185, 129, 0.12)", color: "#10b981" },
  INVOICE_ISSUED: { bg: "rgba(59, 130, 246, 0.12)", color: "#3b82f6" },
  INVOICE_CANCELLED: { bg: "rgba(239, 68, 68, 0.12)", color: "#ef4444" },
  INVOICE_UPDATED: { bg: "rgba(245, 158, 11, 0.12)", color: "#f59e0b" },
  TAX_SYSTEM_CREATED: { bg: "rgba(168, 85, 247, 0.12)", color: "#a855f7" },
  TAX_SYSTEM_UPDATED: { bg: "rgba(168, 85, 247, 0.12)", color: "#a855f7" },
  TAX_SYSTEM_SELECTED: { bg: "rgba(79, 70, 229, 0.12)", color: "#4f46e5" },
  SALE_CREATED: { bg: "rgba(16, 185, 129, 0.12)", color: "#10b981" },
  SALE_UPDATED: { bg: "rgba(245, 158, 11, 0.12)", color: "#f59e0b" },
  SALE_DELETED: { bg: "rgba(239, 68, 68, 0.12)", color: "#ef4444" },
};

const ACTION_CHOICES = [
  "INVOICE_CREATED",
  "INVOICE_ISSUED",
  "INVOICE_CANCELLED",
  "INVOICE_UPDATED",
  "TAX_SYSTEM_CREATED",
  "TAX_SYSTEM_UPDATED",
  "TAX_SYSTEM_SELECTED",
  "SALE_CREATED",
  "SALE_UPDATED",
  "SALE_DELETED",
];

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-RW", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

function AuditLogs() {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterAction, setFilterAction] = useState("");
  const [expandedRow, setExpandedRow] = useState(null);
  const searchTimer = useRef(null);

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page: currentPage, page_size: 30 };
      if (debouncedSearch) params.search = debouncedSearch;
      if (filterAction) params.action = filterAction;

      const response = await invoicingAPI.listAuditLogs(params);
      const data = response?.data || response;

      if (Array.isArray(data)) {
        setLogs(data);
        setPagination(null);
      } else if (data?.results) {
        setLogs(data.results);
        setPagination({
          total: data.count,
          page: currentPage,
          pages: Math.ceil(data.count / 30),
        });
      } else if (data?.logs) {
        setLogs(data.logs);
        setPagination(data.pagination || null);
      } else {
        setLogs([]);
      }
      setError(null);
    } catch (err) {
      setError(err?.message || "Failed to load audit logs");
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearch, filterAction]);

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(searchTimer.current);
  }, [searchTerm]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const toggleExpand = (logId) => {
    setExpandedRow(expandedRow === logId ? null : logId);
  };

  const renderActionBadge = (action) => {
    const style = ACTION_COLORS[action] || { bg: "rgba(107,114,128,0.12)", color: "#6b7280" };
    return (
      <span
        className="action-badge"
        style={{ backgroundColor: style.bg, color: style.color }}
      >
        {action?.replace(/_/g, " ") || "UNKNOWN"}
      </span>
    );
  };

  return (
    <div className="page-content audit-logs-page">
      <div className="section-header">
        <h2>
          <FiShield style={{ marginRight: 8 }} />
          Audit Logs
        </h2>
        <button className="btn btn-ghost" onClick={loadLogs}>
          <FiRefreshCw /> Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="audit-filters">
        <div className="search-input-wrapper">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by user, object, organization..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="clear-search" onClick={() => setSearchTerm("")}>
              <FiX />
            </button>
          )}
        </div>

        <div className="filter-group">
          <FiFilter size={14} />
          <select
            value={filterAction}
            onChange={(e) => {
              setFilterAction(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All Actions</option>
            {ACTION_CHOICES.map((a) => (
              <option key={a} value={a}>
                {a.replace(/_/g, " ")}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <ErrorState message={error} onRetry={loadLogs} />
      ) : logs.length === 0 ? (
        <EmptyState
          title="No Audit Logs"
          message="No audit log entries found matching your criteria."
        />
      ) : (
        <>
          <div className="audit-table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>User</th>
                  <th>Action</th>
                  <th>Object</th>
                  <th>Organization</th>
                  <th>IP Address</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <React.Fragment key={log.id}>
                    <tr
                      className={`audit-row ${expandedRow === log.id ? "expanded" : ""}`}
                      onClick={() => toggleExpand(log.id)}
                      style={{ cursor: "pointer" }}
                    >
                      <td>
                        <span className="timestamp">
                          <FiClock size={12} />
                          {formatDate(log.timestamp)}
                        </span>
                      </td>
                      <td>
                        <span className="user-cell">
                          <FiUser size={12} />
                          {log.user_email || log.user?.email || "System"}
                        </span>
                      </td>
                      <td>{renderActionBadge(log.action)}</td>
                      <td>
                        <span className="object-cell">
                          {log.object_type}
                          {log.object_id && (
                            <span className="object-id">
                              {String(log.object_id).substring(0, 8)}...
                            </span>
                          )}
                        </span>
                      </td>
                      <td>{log.organization_name || log.organization?.name || "—"}</td>
                      <td>{log.ip_address || "—"}</td>
                    </tr>
                    {expandedRow === log.id && (
                      <tr className="audit-detail-row">
                        <td colSpan={6}>
                          <div className="audit-detail">
                            {log.previous_value && (
                              <div className="detail-block">
                                <strong>Previous Value:</strong>
                                <pre>{JSON.stringify(log.previous_value, null, 2)}</pre>
                              </div>
                            )}
                            {log.new_value && (
                              <div className="detail-block">
                                <strong>New Value:</strong>
                                <pre>{JSON.stringify(log.new_value, null, 2)}</pre>
                              </div>
                            )}
                            {!log.previous_value && !log.new_value && (
                              <p className="no-detail">No additional details available.</p>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {pagination && pagination.pages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={pagination.pages}
              onPageChange={setCurrentPage}
            />
          )}
        </>
      )}
    </div>
  );
}

export default AuditLogs;
